import React, { useMemo, useState } from "react";
import { Button } from "@design-system/Button/Button";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useBalanceSheetQuery } from "@api/accountingReports.api";
import { useJeConversionInProgress } from "@hooks/useJeConversionInProgress";
import { AccountingReportType } from "@common/types/reports/report-type";
import { ReportDataProcessingBanner } from "@components/accounting/ReportDataProcessingBanner";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { GlDrilldownDrawer } from "@components/accounting/generalLedger/GlDrilldownDrawer";
import { AccountingReportFiltersForm } from "../AccountingReportFiltersForm";
import { balanceSheetFilterDefinition } from "../accountingReportFilterDefinitions";
import { AccountingReportViewToolbar } from "../reportViews/AccountingReportViewToolbar";
import { useAccountingTabContext } from "../useAccountingTab";
import { useConsolidatedGlDrillDown } from "../useConsolidatedGlDrillDown";
import { useConsolidatedReportView } from "../useConsolidatedReportView";
import { useGlDrilldownDrawerState } from "../useGlDrilldownDrawerState";
import { useBalanceSheetDownload } from "./useBalanceSheetDownload";
import { BalanceSheetContent } from "./BalanceSheetContent";
import { BalanceSheetAccountMappingModal } from "./BalanceSheetImporter/BalanceSheetAccountMappingModal";
import { BalanceSheetImporterModal } from "./BalanceSheetImporter/BalanceSheetImporterModal";
import { DownloadDropdown } from "./DownloadDropdown";

export const BalanceSheetPreviewPage: React.FC = () => {
    const { filters, setFilters, selectedEntity, ledgerBasis } =
        useAccountingTabContext();
    const {
        openDrillDown,
        closeDrillDown,
        isOpen: isGlDrawerOpen,
        drawerKey,
        drawerTarget,
    } = useGlDrilldownDrawerState();
    const { consolidationGroup, isConsolidated } = useConsolidatedReportView({
        filters,
        setFilters,
    });
    const toGlDrillDownTarget = useConsolidatedGlDrillDown({
        consolidationGroup,
        isConsolidated,
        ledgerBasis,
    });
    const glDrillDownTarget = useMemo(
        () => (drawerTarget ? toGlDrillDownTarget(drawerTarget) : null),
        [drawerTarget, toGlDrillDownTarget],
    );
    const filtersWithDefaultValues = useMemo(() => {
        if (filters.cycle === undefined) {
            return {
                ...filters,
                cycle: balanceSheetFilterDefinition.defaultCycle,
            };
        }
        return filters;
    }, [filters]);

    const {
        balanceSheetImporter: balanceSheetImporterEnabled,
        balanceSheetAccountMapping: balanceSheetAccountMappingEnabled,
    } = useTypedFlags();

    const { isJeConversionInProgress } = useJeConversionInProgress(
        selectedEntity?.id,
    );

    const { balanceSheet, generationDate } = useBalanceSheetQuery(
        filtersWithDefaultValues,
    );

    const { isDownloading, download } = useBalanceSheetDownload(
        filtersWithDefaultValues,
        balanceSheet.data?.presentation?.rowVisibility,
    );

    const [showBalanceSheetImporterModal, setShowBalanceSheetImporterModal] =
        useState(false);
    const [showAccountMappingModal, setShowAccountMappingModal] =
        useState(false);

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_BALANCE_SHEET_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    return (
        <>
            {isJeConversionInProgress && <ReportDataProcessingBanner />}
            <AccountingReportViewToolbar
                reportId="balanceSheet"
                filters={filtersWithDefaultValues}
                setFilters={setFilters}
            >
                {balanceSheetImporterEnabled && (
                    <Button
                        variant="primary"
                        size="md"
                        onClick={() => setShowBalanceSheetImporterModal(true)}
                    >
                        Import balances
                    </Button>
                )}
                {balanceSheetAccountMappingEnabled && (
                    <Button
                        variant="primary"
                        size="md"
                        onClick={() => setShowAccountMappingModal(true)}
                    >
                        Import & map accounts
                    </Button>
                )}
                <DownloadDropdown
                    onDownload={(format) =>
                        selectedEntity &&
                        download({
                            entity: selectedEntity,
                            format,
                            date: filtersWithDefaultValues.endDate,
                        })
                    }
                    loading={isDownloading}
                    disabled={balanceSheet.isLoading || !balanceSheet.isSuccess}
                />
            </AccountingReportViewToolbar>
            <AccountingReportFiltersForm
                {...balanceSheetFilterDefinition.filterProps}
                showFinancialAccountFilter={!isConsolidated}
                filters={filtersWithDefaultValues}
                setFilters={setFilters}
                consolidationGroup={consolidationGroup}
                pinnedPresets={pinnedPresets}
                onTogglePin={onTogglePin}
                shouldTrackDatePreset
                hideSearch
                hasEntityGrouping
                hasClassGrouping
                requireDateSelection
                hideLedgerSelect={isConsolidated}
                showCyclesSelect={!isConsolidated}
                reportType={AccountingReportType.BALANCE_SHEET}
            />
            <LoaderOnInitialLoading queryResult={balanceSheet} dimWhileFetching>
                {balanceSheet.data && selectedEntity && (
                    <BalanceSheetContent
                        report={balanceSheet.data}
                        filters={filtersWithDefaultValues}
                        chrome={balanceSheet.data.presentation.chrome}
                        numberFormat={
                            balanceSheet.data.presentation.numberFormat
                        }
                        generationDate={generationDate}
                        onDrillDown={openDrillDown}
                    />
                )}
            </LoaderOnInitialLoading>

            <BalanceSheetImporterModal
                show={showBalanceSheetImporterModal}
                onHide={() => setShowBalanceSheetImporterModal(false)}
            />

            {showAccountMappingModal && (
                <BalanceSheetAccountMappingModal
                    show={showAccountMappingModal}
                    onHide={() => setShowAccountMappingModal(false)}
                />
            )}
            {drawerTarget && glDrillDownTarget && (
                <GlDrilldownDrawer
                    key={drawerKey}
                    open={isGlDrawerOpen}
                    onClose={closeDrillDown}
                    filters={glDrillDownTarget.filters}
                    entityIds={drawerTarget.entityIds}
                    ledgerBasis={glDrillDownTarget.ledgerBasis}
                    classifications={drawerTarget.classifications}
                    pnlBalanceSince={drawerTarget.pnlBalanceSince}
                />
            )}
        </>
    );
};
